import moment from "moment";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate, useLocation } from "react-router";
import { Button, Checkbox, Skeleton } from "@mui/material";
import NoDataCard from "../no-data-card";
import { useDispatch } from "react-redux";
import { setOperationTitle } from "../../store/reducers/operation";
import { dateFormater } from "../../bmr/utils/common/utils";
import { useState } from "react";
import BsModal from "../../bmr/components/common/BaseModal";
import ChangeRequestModal from "./changeRequestModal";

const PendingTaskTile = ({
  task = [],
  isClosed,
  canSelect,
  loadMoreData,
  maxPages,
  page,
  loading,
  isDashboard,
  showBoxShadow = false,
  onArchiveTask,
  onUnarchiveTask,
  isArchived,
  refreshAgain,
  onTaskResolved = () => { },
  selectedIds = new Set(),
  setSelectedIds = () => { },
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  // State for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const MODAL_TRIGGER_SLUGS = [
    "change-awb-number",
    "change-tracking-link",
    "change-delivery-partner-id",
    "change-infusion-date",
    "final-change-tracking-link",
    "final-change-awb-number",
    "final-change-delivery-partner-id",
    "kit-change-tracking-link",
    "kit-change-awb-number",
    "kit-change-delivery-partner-id",
    "change-assigned-apheresis-date",
    "change-slot-comment",
    "change-slot-attachment",
    "change-publish-product",
    "change-batch-no-details",
    "change-scheduled-aphresis-date-range",
    "change-financial-feasibility",
    "change-clinical-feasibility",
    "change-terminate-cycle-status",
    "change-patient-gender",
    "change-patient-name",
    "change-patient-date-of-birth",
    "change-publish-product-comment",
    "complete-infusion-date",
    "change-publish-product-date",
    "change-dead-to-alive", "change-lost-to-follow-up-to-alive", "change-date-of-lost-to-follow-up", "change-date-of-death", "change-lost-to-follow-up-date", "change-dead-to-lost-to-follow-up", "change-lost-to-follow-up-to-dead"
  ];



  const handleArchiveSelected = () => {
    if (onArchiveTask && selectedIds.size > 0) {
      onArchiveTask(selectedIds);
    }
  };
  const handleUnarchiveSelected = () => {
    if (onUnarchiveTask && selectedIds.size > 0) {
      onUnarchiveTask(selectedIds);
    }
  };
  const shouldOpenModal = (taskDetails) => {
    const taskSlugs = taskDetails?.change_request_reason_value?.map(item => item.slug) || [];
    const shouldOpen = taskSlugs.some(slug => MODAL_TRIGGER_SLUGS.includes(slug));
    return shouldOpen;
  };
  // Function to get button text based on task
  const getButtonText = (taskDetails) => {
    const taskSlugs = taskDetails?.change_request_reason_value?.map(item => item.slug) || [];
    const firstSlug = taskSlugs[0]; // Get the first slug for comparison
    const isPending = taskDetails?.status === 0 || taskDetails?.status === "0";
    if (isPending) {
      // Check for specific slugs that need custom button text
      if (firstSlug === "assign-infusion") {
        return "Assign Infusion Date";
      } else if (firstSlug === "payment-status") {
        return "Update Payment Status";
      } else if (firstSlug === "lsm-rejection") {
        return "Restart Apheresis";
      } else if (shouldOpenModal(taskDetails)) {
        return "View Change Request";
      }
    }
    return "View Task";
  };

  const handleApproveReject = async (taskDetails) => {
    setSelectedTask(taskDetails);
    onTaskResolved(taskDetails?.id, taskDetails);
    // Keep UI in sync immediately; then reconcile with backend shortly after.
    setTimeout(() => {
      refreshAgain();
    }, 1200);
    return;
  }
  const handleTaskView = async (taskDetails, openModal = false) => {
    const batchNo = taskDetails?.batch_no ? '?batch=' + taskDetails?.batch_no : '';

    if (shouldOpenModal(taskDetails) && !openModal && taskDetails?.status != '1') {
      setSelectedTask(taskDetails);
      setModalOpen(true);
      return;
    }
    // Original navigation logic for non-modal tasks
    const prodDetailsId = taskDetails?.phase?.product_details?.id;
    const phaseId = taskDetails?.patient?.id;
    const qrCodeId = taskDetails?.qrCode?.id;
    const subjectId = taskDetails?.subject_id;
    const PhaseDetailId = taskDetails?.phase?.phase_type_details?.id;
    const operationTitle =
      taskDetails?.change_request_reason_value?.[0]?.slug || "";
    if (taskDetails?.status == "0") {
      await dispatch(setOperationTitle(operationTitle));
    }
    // Check if we're currently in BMR section
    const isBMRSection = location.pathname.includes("/bmr/");
    if (operationTitle == "change-terminate-cycle-status") {
      navigate(`/bmr/manufacturer_view/${taskDetails?.qrCode?.id}/${taskDetails?.feasibility_assessments_id}?batch=${batchNo}`);
    } else if (operationTitle == "change-scheduled-aphresis-date-range") {
      navigate(`/bmr/feasibility-assessment/subjects/details/${phaseId}${batchNo}`);
    } else if (
      ["change-financial-feasibility", "change-clinical-feasibility"].includes(
        operationTitle
      )
    ) {
      navigate(
        `/bmr/feasibility-assessment/subjects/details/${phaseId}${batchNo}`,
      );
    } else if (
      ["change-delivery-partner-id", "change-tracking-link", "change-awb-number"].includes(
        operationTitle,
      )
    ) {
      navigate(
        `/phases/phase-details/${PhaseDetailId}/patient-details/${subjectId}/inbound-form/${qrCodeId}`,
      );
    } else {
      // Navigate based on current section
      if (isBMRSection) {
        navigate(
          `/bmr/products/product-details/${prodDetailsId}/patient-details/${phaseId}${batchNo}`
        );
      } else {
        navigate(
          `/products/product-details/${prodDetailsId}/patient-details/${phaseId}${batchNo}`
        );
      }
    }
  };

  const getTaskColorAndDate = (createdAt, isClosed) => {
    const today = moment().startOf("day");
    const createdDate = moment(createdAt).startOf("day");
    const diffInDays = today.diff(createdDate, "days");

    if (isClosed) {
      return {
        taskColor: "#00B278",
        dateText:
          diffInDays === 0
            ? "Today"
            : `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`,
      };
    }

    if (diffInDays === 0) {
      return {
        taskColor: "#3b82f6",
        dateText: "Today",
      };
    } else if (diffInDays <= 3) {
      return {
        taskColor: "#f97316",
        dateText: `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`,
      };
    }

    return {
      taskColor: "#ef4444",
      dateText: `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`,
    };
  };

  // Close modal handler
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTask(null);
  };

  return (
    <>
      <BsModal
        open={modalOpen}
        onClose={handleCloseModal}
        size="md"
        fullWidth={false}
      >
        <ChangeRequestModal
          isLoadingBtn={loading}
          taskData={selectedTask}
          onClose={handleCloseModal}
          onApprove={handleApproveReject}
          onReject={handleApproveReject}
        />
      </BsModal>

      {loading ? (
        <Skeleton height={"150px"} />
      ) : !loading && task?.length === 0 ? (
        <div className="col-start-4 col-end-13">
          <NoDataCard heading="No Data to Display" />
        </div>
      ) : (
        // <>
        <div className="flex flex-col " style={{ height: "100%", minHeight: 0, overflow: "hidden" }}>
          <div
            id="scrollableTaskContainer"
            style={{ flex: 1, overflowY: "auto", minHeight: 0 }}
          >
            <InfiniteScroll
              dataLength={task?.length || 0}
              next={loadMoreData}
              hasMore={page < maxPages}
              scrollableTarget="scrollableTaskContainer"
            >
              {task.map((op, index) => {
                const { taskColor, dateText } = getTaskColorAndDate(
                  op?.createdAt,
                  isClosed
                );
                const isChecked = selectedIds.has(op.id);
                const opPhaseTypeName = (op?.phase?.phase_type_details?.name || "").toLowerCase();
                const opPersonLabel = opPhaseTypeName === "trial" ? "Subject" : "Patient";
                return (
                  <div
                    key={index}
                    className="bg-white flex flex-col md:flex-row justify-between rounded-md p-2 pt-2 border-l-4 mb-2 p-3"
                    style={{
                      borderLeftWidth: "2px",
                      borderLeftColor: isClosed ? "#00B278" : taskColor,
                      boxShadow: showBoxShadow ? "0px 2px 8px rgba(0, 0, 0, 0.1)" : "none"
                    }}
                  >
                    {canSelect && (
                      <div className="flex items-center pl-2 pr-1">
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) => {
                            const checked = e.target.checked;

                            setSelectedIds(prev => {
                              const next = new Set(prev);
                              if (checked) next.add(op.id);
                              else next.delete(op.id);
                              return next;
                            });
                          }}
                          size="small"
                        />
                      </div>
                    )}
                    <div className="task-start w-full md:w-[50%] pr-0 md:pr-4">
                      <div>
                        <span className="font-bold text-sm md:text-base block mb-1 capitalize">
                          {op?.phase?.product_details?.name} {op?.phase?.title}
                        </span>
                        <div className="text-sm text-gray-400 pb-1">
                          In {op?.changeRequestReason?.title}{" "}
                          {op?.creator?.name} wants to{" "}
                          {Array.isArray(op?.change_request_reason_value)
                            ? op.change_request_reason_value
                              .map((reas) => reas.title)
                              .join(", ")
                            : "--"}{" "}
                          for {op?.patient?.first_name} {op?.patient?.last_name}
                          ({opPersonLabel}) for {op?.phase?.product_details?.name} (Product) and {op?.phase?.title} (Phase) having {opPersonLabel.toLowerCase()} id {op?.patient?.patient_id}
                        </div>
                        <div>
                          {op.createdAt ? (
                            <>
                              {dateFormater(op.createdAt)}{" "}
                              <span
                                style={{
                                  color: isClosed ? "#00B278" : taskColor,
                                }}
                              >
                                ({dateText})
                              </span>
                            </>
                          ) : (
                            "--"
                          )}
                        </div>
                      </div>
                    </div>

                    <div
                      className={
                        !isClosed
                          ? "mr-5 justify-end w-full md:w-[50%] flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 mt-3 sm:mt-0"
                          : "w-full md:w-[50%] flex flex-col sm:flex-row justify-around items-start sm:items-center gap-3 sm:gap-5 mt-3 sm:mt-0"
                      }
                    >
                      {isClosed && (
                        <>
                          <div>
                            <p className="text-gray-500">{op.cr_status == 0 ? "Approved" : op.cr_status == 1 ? "Rejected" : "Closed"} By</p>
                            <p className="font-bold text-sm text-gray-900 truncate capitalize">
                              {op?.cr_closed_by ? op?.cr_closed_by?.name : "--"}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">{op.cr_status == 0 ? "Approved" : op.cr_status == 1 ? "Rejected" : "Closing"} Date</p>
                            <p className="font-bold text-sm text-gray-900 truncate">
                              {op?.closed_date
                                ? dateFormater(op?.closed_date)
                                : "--"}
                            </p>
                          </div>
                        </>
                      )}
                      {!isDashboard && (
                        <Button
                          size="medium"
                          variant="contained"
                          onClick={() => handleTaskView(op)}
                          className="pl-2 pr-2 sm:pl-5 sm:pr-5 w-32 sm:w-40 h-10"
                        >
                          {getButtonText(op)}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </InfiniteScroll>
          </div>
          {selectedIds.size > 0 && canSelect && (
            <div 
             className="sticky z-10 bg-white border-t p-3 flex justify-end  absolute bottom-0 "
             >
              <Button
                variant="contained"
                onClick={isArchived === 1 ? handleUnarchiveSelected : handleArchiveSelected}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                }}
              >
                {isArchived === 1 ? "Unarchive Selected Task" : "Archive Selected Task"}
              </Button>
            </div>
          )}
        </div>

      )}
    </>
  );
};

export default PendingTaskTile;